import { useState, useCallback, useMemo, useEffect } from 'react';
import { Pressable, View, TextInput } from 'react-native';
import { Copy as CopyIcon, CornerUpRight, Trash2 } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { Icon as UiIcon } from '@/components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MealUtils, type MealLike } from '@fytness/shared';
import { useTranslation } from 'react-i18next';
import { StringUtils } from '~/global/utils/string.utils';
import { UnitDropdown } from './UnitDropdown';

export interface MealLogCardMeal {
  id: string;
  name: string;
  brand?: string | null;
  dataSource?: string | null;
  servingSize?: number | null;
  servingUnit?: string | null;
  servingUnits?: Array<{
    id: string;
    name: string;
    weightGrams: number;
    sortIndex: number;
  }>;
  nutritionalInfo?: {
    energyKcal?: number | null;
    protein?: number | null;
    carbs?: number | null;
    fats?: number | null;
    fibre?: number | null;
  } | null;
}

export interface MealLogCardProps {
  meal: MealLogCardMeal;
  consumedAmount: number;
  /** Unit the log was recorded in (a servingUnits name); null = base unit. */
  consumedAmountUnit?: string | null;
  mealLogId: string;
  onRemove: (mealLogId: string) => void;
  onAmountChange: (
    mealLogId: string,
    amount: number,
    unit: string | null,
  ) => void;
  /**
   * Copy this single log elsewhere. Optional: when supplied it adds a Copy entry
   * to the ⋯ menu (which always carries Delete). The AI chat's own `MealLogCard`
   * is a different component with a narrower contract and is unaffected.
   */
  onCopy?: (mealLogId: string) => void;
  /** Move this single log elsewhere. See {@link MealLogCardProps.onCopy}. */
  onMove?: (mealLogId: string) => void;
  /**
   * Selection mode: renders a leading checkbox and HIDES the ⋯ menu (the bulk
   * actions live on the selection bar while a selection is being assembled).
   */
  selectable?: boolean;
  /** Whether this card is part of the current selection. */
  selected?: boolean;
  /** Toggle this card's membership of the selection. */
  onToggleSelect?: (mealLogId: string) => void;
}

/**
 * Map a stored unit name back to a unit-picker option id. `null`/unknown ⇒ base
 * (grams); a name matching one of the meal's serving units ⇒ that unit's id.
 */
const resolveUnitId = (
  units: MealLogCardMeal['servingUnits'],
  unitName: string | null | undefined,
): string => {
  if (!unitName) return 'base';
  const match = (units ?? []).find(
    (u) => u.name.toLowerCase() === unitName.toLowerCase(),
  );
  return match ? match.id : 'base';
};

export const MealLogCard = ({
  meal,
  consumedAmount,
  consumedAmountUnit,
  mealLogId,
  onRemove,
  onAmountChange,
  onCopy,
  onMove,
  selectable = false,
  selected = false,
  onToggleSelect,
}: MealLogCardProps) => {
  const { t } = useTranslation('meal');
  const [isEditing, setIsEditing] = useState(false);

  // Restore the unit the user logged in (persisted on the log). Re-sync if the
  // persisted unit or the meal's serving units change.
  const initialUnitId = useMemo(
    () => resolveUnitId(meal.servingUnits, consumedAmountUnit),
    [meal.servingUnits, consumedAmountUnit],
  );
  const [selectedUnitId, setSelectedUnitId] = useState(initialUnitId);
  useEffect(() => {
    setSelectedUnitId(initialUnitId);
  }, [initialUnitId]);

  const nutrition = useMemo(
    () => MealUtils.adjustedNutrition(meal as MealLike, consumedAmount),
    [meal, consumedAmount],
  );

  // Base unit is always grams (100g base). Additional named serving units
  // (e.g. "Bowl" = 250g) are appended after it, ordered by sortIndex.
  const baseLabel = t(`units.${meal.servingUnit ?? 'GRAMS'}.shortSign`);
  const unitOptions = useMemo(() => {
    const base = { id: 'base', label: baseLabel, weightGrams: 1 };
    const units = (meal.servingUnits ?? [])
      .slice()
      .sort((a, b) => a.sortIndex - b.sortIndex)
      .map((u) => ({ id: u.id, label: u.name, weightGrams: u.weightGrams }));
    return [base, ...units];
  }, [meal.servingUnits, baseLabel]);
  const hasUnitPicker = (meal.servingUnits?.length ?? 0) > 0;

  const selectedUnit =
    unitOptions.find((u) => u.id === selectedUnitId) ?? unitOptions[0];

  // The unit name persisted alongside the amount: null for the base unit (grams),
  // otherwise the serving-unit's name (e.g. "Package").
  const selectedUnitName =
    selectedUnit.id === 'base' ? null : selectedUnit.label;

  // consumedAmount is stored in grams; the picker only changes how it is shown.
  const displayedAmount = consumedAmount / selectedUnit.weightGrams;
  const formatQty = (n: number) => (Math.round(n * 100) / 100).toString();
  const [editValue, setEditValue] = useState(formatQty(displayedAmount));

  const handleEditStart = useCallback(() => {
    setEditValue(formatQty(displayedAmount));
    setIsEditing(true);
  }, [displayedAmount]);

  const handleEditEnd = useCallback(() => {
    setIsEditing(false);
    const num = StringUtils.parseFloat(editValue);
    if (num !== undefined && !isNaN(num) && num > 0) {
      onAmountChange(
        mealLogId,
        num * selectedUnit.weightGrams,
        selectedUnitName,
      );
    } else {
      setEditValue(formatQty(displayedAmount));
    }
  }, [
    editValue,
    mealLogId,
    onAmountChange,
    selectedUnit.weightGrams,
    selectedUnitName,
    displayedAmount,
  ]);

  const stepBy = useCallback(
    (delta: number) => {
      const next = displayedAmount + delta;
      if (next <= 0) return;
      onAmountChange(
        mealLogId,
        next * selectedUnit.weightGrams,
        selectedUnitName,
      );
    },
    [
      displayedAmount,
      mealLogId,
      onAmountChange,
      selectedUnit.weightGrams,
      selectedUnitName,
    ],
  );

  // Changing the unit doesn't change the stored grams — it persists which unit the
  // user is logging in, so the choice survives a reload.
  const handleUnitSelect = useCallback(
    (unitId: string) => {
      setSelectedUnitId(unitId);
      const opt = unitOptions.find((u) => u.id === unitId) ?? unitOptions[0];
      const unitName = opt.id === 'base' ? null : opt.label;
      onAmountChange(mealLogId, consumedAmount, unitName);
    },
    [unitOptions, onAmountChange, mealLogId, consumedAmount],
  );

  const macros = [
    { value: nutrition.carbs, dotClassName: 'bg-macro-carbs' },
    { value: nutrition.protein, dotClassName: 'bg-macro-protein' },
    { value: nutrition.fats, dotClassName: 'bg-macro-fats' },
  ];

  const source = meal.dataSource ?? meal.brand ?? null;

  const servingUnitLabel = meal.servingUnit
    ? t(`units.${meal.servingUnit}.shortSign`)
    : '';

  return (
    <View
      testID="meal-log-card"
      collapsable={false}
      className={`overflow-hidden rounded-2xl ${
        selectable && selected ? 'bg-accent-soft' : 'bg-surface'
      }`}
    >
      {/* Top row: title + inline macros (left), kcal (right) */}
      <View className="flex flex-row items-center gap-3 px-4 py-3.5">
        {selectable ? (
          <Pressable
            testID="meal-log-card-select"
            collapsable={false}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: selected }}
            onPress={() => onToggleSelect?.(mealLogId)}
            hitSlop={8}
            className={`h-5 w-5 items-center justify-center rounded-md ${
              selected ? 'bg-accent' : 'bg-surface-elevated'
            }`}
          >
            {selected ? (
              <Icon name="Check" size={14} className="text-background" />
            ) : null}
          </Pressable>
        ) : null}
        <View className="flex-1 gap-1">
          <Text
            testID="meal-log-card-name"
            className="text-[15px] font-sora-semibold text-text-primary"
          >
            {meal.name}
          </Text>
          <View className="flex flex-row flex-wrap items-center gap-x-2.5 gap-y-1">
            {macros.map(({ value, dotClassName }, index) => (
              <View key={index} className="flex flex-row items-center gap-1">
                <View
                  className={`h-[5px] w-[5px] rounded-[3px] ${dotClassName}`}
                />
                <Text className="text-[11px] font-inter-medium text-text-muted">
                  {value != null ? `${Math.round(value)}g` : '-'}
                </Text>
              </View>
            ))}
            {source && (
              <>
                <Text className="text-[11px] text-text-muted">·</Text>
                <Text className="text-[11px] font-inter-medium text-text-muted">
                  {source}
                </Text>
              </>
            )}
          </View>
        </View>
        <View className="items-end">
          <Text className="text-xl font-sora-bold text-text-primary">
            {nutrition.calories != null ? Math.round(nutrition.calories) : '-'}
          </Text>
          <Text className="text-[10px] font-inter-medium text-text-muted">
            {t('nutrition.kcal.unit')}
          </Text>
        </View>
      </View>

      <View className="h-px w-full bg-border" />

      {/* Bottom row: quantity stepper + unit selector + delete */}
      <View className="flex flex-row items-center gap-2 px-3 py-2.5">
        <View className="w-24 flex flex-row items-center justify-between rounded-lg bg-surface-elevated px-3 py-2">
          <Pressable
            testID="meal-log-card-decrement"
            collapsable={false}
            onPress={() => stepBy(-1)}
            hitSlop={8}
            className="active:opacity-70"
          >
            <Icon name="Minus" size={16} className="text-text-muted" />
          </Pressable>
          {isEditing ? (
            <TextInput
              value={editValue}
              onChangeText={setEditValue}
              onBlur={handleEditEnd}
              onSubmitEditing={handleEditEnd}
              autoFocus
              keyboardType="decimal-pad"
              className="text-sm font-inter-medium text-text-primary"
              style={{ padding: 0, minWidth: 24, textAlign: 'center' }}
            />
          ) : (
            <Pressable onPress={handleEditStart} hitSlop={8}>
              <Text
                testID="meal-log-card-amount"
                className="text-sm font-inter-medium text-text-primary"
              >
                {formatQty(displayedAmount)}
              </Text>
            </Pressable>
          )}
          <Pressable
            testID="meal-log-card-increment"
            collapsable={false}
            onPress={() => stepBy(1)}
            hitSlop={8}
            className="active:opacity-70"
          >
            <Icon name="Plus" size={16} className="text-text-muted" />
          </Pressable>
        </View>

        {hasUnitPicker ? (
          <UnitDropdown
            testID="meal-log-card-unit"
            options={unitOptions}
            selectedId={selectedUnit.id}
            onSelect={handleUnitSelect}
          />
        ) : servingUnitLabel ? (
          <View
            testID="meal-log-card-unit"
            className="flex flex-row items-center rounded-[10px] bg-surface-elevated px-3 py-2"
          >
            <Text className="text-[13px] font-inter-semibold text-text-primary">
              {servingUnitLabel}
            </Text>
          </View>
        ) : null}

        <View className="flex-1" />

        {/*
         * A single ⋯ button rather than a `RowMenu` (DD9): `RowMenu` requires an
         * `onSelect` row tap this card does not have, and it makes the whole row
         * a long-press target — which collides with the amount `TextInput`'s own
         * text-selection gesture, and on iOS would preview a card containing a
         * focused input in the native context menu. Delete lives in this menu
         * too (per the approved design), so the ⋯ is the card's only action
         * affordance outside selection mode.
         */}
        {!selectable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Pressable
                testID="meal-log-card-actions"
                collapsable={false}
                accessibilityLabel={t('copy.actionsLabel')}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] active:opacity-70"
              >
                <Icon name="Ellipsis" size={16} className="text-text-muted" />
              </Pressable>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onCopy ? (
                <DropdownMenuItem
                  testID="meal-log-copy"
                  collapsable={false}
                  onPress={() => onCopy(mealLogId)}
                >
                  <UiIcon as={CopyIcon} size={16} className="text-foreground" />
                  <Text>{t('copy.action')}</Text>
                </DropdownMenuItem>
              ) : null}
              {onMove ? (
                <DropdownMenuItem
                  testID="meal-log-move"
                  collapsable={false}
                  onPress={() => onMove(mealLogId)}
                >
                  <UiIcon
                    as={CornerUpRight}
                    size={16}
                    className="text-foreground"
                  />
                  <Text>{t('copy.moveAction')}</Text>
                </DropdownMenuItem>
              ) : null}
              {onCopy || onMove ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                testID="meal-log-card-remove"
                collapsable={false}
                variant="destructive"
                onPress={() => onRemove(mealLogId)}
              >
                <UiIcon as={Trash2} size={16} className="text-destructive" />
                <Text>{t('copy.deleteAction')}</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </View>
    </View>
  );
};
